import {
  styled,
  secondaryFontFamily,
  useStyledTheme,
} from '@openeducation/oe-it-themes';
import React, { memo, useEffect, useState } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import {
  AnimatedCircularProgress,
  AnimatedCircularProgressProps,
} from 'react-native-circular-progress';

import { OEText } from '../../atoms/oe-text/oe-text';
import { flex } from '../../style-utils';

export interface LevelCircularProgressProps
  extends AnimatedCircularProgressProps {
  currentValue: number;
  ofLabel: string;
  maxValue: number | string;
  description?: string;
  currentValueColor?: string;
  valueFontSize?: number;
  maxValueFontSize?: number;
  style?: StyleProp<ViewStyle>;
  circularStyle?: StyleProp<ViewStyle>;
}

const LevelCircularProgress = ({
  currentValue,
  ofLabel,
  maxValue,
  description,
  valueFontSize = 42,
  maxValueFontSize = 24,
  style,
  circularStyle,
  currentValueColor,
  fill,
  ...rest
}: LevelCircularProgressProps) => {
  const theme = useStyledTheme();

  const [currentFill, setCurrentFill] = useState(0);
  useEffect(() => {
    if (isNaN(fill)) {
      setCurrentFill(0);
      return;
    }
    if (fill > 100) {
      setCurrentFill(100);
      return;
    }
    if (fill < 0) {
      setCurrentFill(0);
      return;
    }
    setCurrentFill(fill);
  }, [fill]);

  return (
    <View style={[flex('align-items-center', 'justify-content-center'), style]}>
      <CircleWrapper size={rest.size as number} style={circularStyle}>
        <AnimatedCircularProgress
          {...rest}
          width={rest.width || 8}
          rotation={0}
          backgroundWidth={rest.backgroundWidth || 5}
          backgroundColor={rest.backgroundColor || theme.color.border}
          lineCap="round"
          fill={currentFill}
        >
          {() => (
            <>
              <Value
                color={currentValueColor || rest.tintColor}
                fontSize={valueFontSize}
              >
                {currentValue}
              </Value>
              <MaxValue fontSize={maxValueFontSize}>
                {ofLabel} {maxValue}
              </MaxValue>
            </>
          )}
        </AnimatedCircularProgress>
      </CircleWrapper>
      {!!description && <Description>{description}</Description>}
    </View>
  );
};

const CircleWrapper = styled.View<{ size?: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: ${({ size }) => size && size / 2}px;
  background-color: ${({ theme }) => theme.color.background};
`;

const Value = styled(OEText).attrs({
  fontFamily: secondaryFontFamily,
  fontType: 'bold',
  textAlign: 'center',
})`
  margin-top: -10px;
  margin-bottom: -10px;
`;

const MaxValue = styled(OEText).attrs({
  fontFamily: secondaryFontFamily,
  textAlign: 'center',
})``;

const Description = styled(OEText).attrs(({ theme }) => ({
  fontFamily: secondaryFontFamily,
  textAlign: 'center',
  color: theme.color.black,
  fontType: 'medium',
  fontSize: 18,
}))`
  margin-top: 12px;
`;

export default memo(LevelCircularProgress);
